// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	fetchBatch,
	type CyciProfile,
} from '@app/features/user/commands/CyciProfileCommands';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const BATCH_DELAY_MS = 25;
const BATCH_SIZE = 100;
const RETRY_DELAY_MS = 30_000;

class CyciProfileFlairStore {
	readonly profiles = observable.map<string, CyciProfile | null>();

	private readonly queued = new Set<string>();
	private readonly inFlight = new Set<string>();
	private readonly retryAfter = new Map<string, number>();

	private flushTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable(
			this,
			{
				profiles: false,
				get: false,
				has: false,
				queued: false,
				inFlight: false,
				retryAfter: false,
				flushTimer: false,
			},
			{autoBind: true},
		);
	}

	get(userId: string): CyciProfile | null {
		return this.profiles.get(userId) ?? null;
	}

	has(userId: string): boolean {
		return this.profiles.has(userId);
	}

	request(userId: string): void {
		if (
			this.profiles.has(userId) ||
			this.queued.has(userId) ||
			this.inFlight.has(userId)
		) {
			return;
		}

		const retryAt = this.retryAfter.get(userId);
		if (retryAt != null && retryAt > Date.now()) {
			return;
		}

		this.queued.add(userId);
		this.scheduleFlush();
	}

	invalidate(userId: string): void {
		this.profiles.delete(userId);
		this.retryAfter.delete(userId);
	}

	set(userId: string, profile: CyciProfile | null): void {
		this.profiles.set(userId, profile);
		this.retryAfter.delete(userId);
	}

	private scheduleFlush(): void {
		if (this.flushTimer != null) {
			return;
		}

		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			void this.flush();
		}, BATCH_DELAY_MS);
	}

	private async flush(): Promise<void> {
		const userIds = Array.from(this.queued).slice(0, BATCH_SIZE);

		if (userIds.length === 0) {
			return;
		}

		for (const userId of userIds) {
			this.queued.delete(userId);
			this.inFlight.add(userId);
		}

		try {
			const result = await fetchBatch(userIds);

			runInAction(() => {
				for (const userId of userIds) {
					this.inFlight.delete(userId);

					// Missing row = this user has no Cyci profile customization.
					this.profiles.set(userId, result[userId] ?? null);
					this.retryAfter.delete(userId);
				}
			});
		} catch {
			const retryAt = Date.now() + RETRY_DELAY_MS;

			runInAction(() => {
				for (const userId of userIds) {
					this.inFlight.delete(userId);
					this.retryAfter.set(userId, retryAt);
				}
			});
		} finally {
			if (this.queued.size > 0) {
				this.scheduleFlush();
			}
		}
	}
}

export default new CyciProfileFlairStore();
